/**
 * Runtime configuration.
 *
 * Deliberately fetched at boot from `/config.json` rather than baked in as
 * VITE_* build-time constants. The three SPAs ship as immutable container
 * images; repointing them at a different Keycloak or realm should not require
 * rebuilding and redeploying three images. The router serves one config.json
 * for the whole platform, so there is exactly one file to change.
 *
 * Path is absolute on purpose: /crm/ and /admin/ are served under a base path,
 * but the config lives at the origin root for all of them.
 */

import type { Brand } from "./brand";

export interface PlatformConfig {
  language?: import("./language").LanguageSettings;
  /** Identity implementation selected by this deployment. */
  authMode: "keycloak" | "platform";
  /** Central platform identity origin, used by cookie auth account actions. */
  identityOrigin: string | null;
  /** Keycloak base URL, no trailing slash, e.g. https://id.example.com */
  keycloakUrl?: string;
  /** Realm name, e.g. "platform" */
  realm?: string;
  /** Public PKCE client shared by every SPA. */
  clientId?: string;
  /** Hub API prefix as seen from the browser. Same origin, so no CORS. */
  apiBase: string;
  /** Shown in the header; lets one image serve more than one company later. */
  companyName: string;
  /** This company's slug, for links and diagnostics. */
  companySlug: string;
  /**
   * True at an address that belongs to NO company — the account home. The
   * server has always said so; the client ignored it until there was something
   * to decide with it. Absent is false: a company's address never sends it.
   */
  isPlatformHost: boolean;
  /** Colours, logo and wordmark. Empty means the platform default. */
  brand: Brand;
  /**
   * The address serving the account home — where "back to my companies" goes.
   *
   * Null when this deployment has configured no platform address. The client
   * renders no back control in that case rather than guessing: a company's own
   * address cannot derive the front door's, and a hostname compiled into the
   * bundle would be one deployment's name shipped to all of them.
   */
  accountHome: string | null;
}

const CONFIG_URL = "/config.json";

let cached: Promise<PlatformConfig> | null = null;

function validate(raw: unknown): PlatformConfig {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("config.json is not an object");
  }
  const c = raw as Record<string, unknown>;
  const authMode = c.authMode === "platform" ? "platform" : "keycloak";
  const required = authMode === "keycloak"
    ? ["keycloakUrl", "realm", "clientId", "apiBase"] as const
    : ["apiBase"] as const;
  for (const key of required) {
    if (typeof c[key] !== "string" || (c[key] as string).length === 0) {
      throw new Error(`config.json is missing "${key}"`);
    }
  }
  const identityOrigin = typeof c.identityOrigin === "string" && c.identityOrigin
    ? c.identityOrigin.replace(/\/+$/, "")
    : null;
  if (identityOrigin && !/^https:\/\//i.test(identityOrigin) &&
      !/^http:\/\/localhost(?::\d+)?$/i.test(identityOrigin)) {
    throw new Error("config.json identityOrigin must use https or localhost http");
  }
  if (authMode === "platform" && !identityOrigin) {
    throw new Error("config.json is missing \"identityOrigin\" for platform auth");
  }
  return {
    language: typeof c.language === "object" && c.language !== null ? c.language as import("./language").LanguageSettings : undefined,
    authMode,
    identityOrigin,
    keycloakUrl: typeof c.keycloakUrl === "string" ? c.keycloakUrl.replace(/\/+$/, "") : undefined,
    realm: typeof c.realm === "string" ? c.realm : undefined,
    clientId: typeof c.clientId === "string" ? c.clientId : undefined,
    apiBase: (c.apiBase as string).replace(/\/+$/, ""),
    // No hardcoded fallback name: this used to default to one customer's, which
    // is exactly the kind of thing docs/CONFIGURABLE-TOOLS.md forbids. An
    // unnamed company shows the neutral product name instead.
    companyName: typeof c.companyName === "string" && c.companyName
      ? c.companyName
      : "Platform",
    companySlug: typeof c.companySlug === "string" ? c.companySlug : "",
    isPlatformHost: c.isPlatformHost === true,
    brand: (typeof c.brand === "object" && c.brand !== null ? c.brand : {}) as Brand,
    // A hostname or nothing. Anything else — a full URL, an empty string —
    // is treated as "not configured", because half a value here becomes a
    // link to somewhere that does not exist.
    accountHome:
      typeof c.accountHome === "string" && /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(c.accountHome)
        ? c.accountHome
        : null,
  };
}

/** Loads (and caches) the platform config. Rejects loudly — a bad config means
 *  login cannot work, and a silent default would be worse than an error page. */
export function loadConfig(): Promise<PlatformConfig> {
  if (!cached) {
    cached = fetch(CONFIG_URL, { cache: "no-store" })
      .then(async (res) => {
        if (!res.ok) throw new Error(`GET ${CONFIG_URL} -> ${res.status}`);
        return validate(await res.json());
      })
      .catch((err) => {
        cached = null; // allow a retry
        throw err;
      });
  }
  return cached;
}
